import React, { useEffect, useState, useMemo } from 'react';
import { IPAnalytics } from '../types';
import { resolveIPs, GeoIPResult } from '../services/geoip';
import { Loader2, MapPin } from 'lucide-react';

const MAP_W = 800;
const MAP_H = 450;
const WORLD_IMG = 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Equirectangular-projection.jpg/1280px-Equirectangular-projection.jpg';

function toXY(lat: number, lng: number): [number, number] {
  const x = ((lng + 180) / 360) * MAP_W;
  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = MAP_H / 2 - (mercN / Math.PI) * (MAP_H / 2);
  return [Math.max(0, Math.min(MAP_W, x)), Math.max(0, Math.min(MAP_H, y))];
}

interface Props {
  ipAnalytics: IPAnalytics[];
  accentColor?: string;
}

export default function IPWorldMap({ ipAnalytics, accentColor = '#f97316' }: Props) {
  const [geoData, setGeoData] = useState<Map<string, GeoIPResult>>(new Map());
  const [loading, setLoading] = useState(false);
  const [hoveredIP, setHoveredIP] = useState<string | null>(null);

  useEffect(() => {
    if (ipAnalytics.length === 0) return;
    let cancelled = false;
    const resolve = async () => {
      setLoading(true);
      const results = await resolveIPs(ipAnalytics.map((ip) => ip.ip_address));
      if (!cancelled) {
        setGeoData(results);
        setLoading(false);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [ipAnalytics]);

  const markers = useMemo(() => {
    return ipAnalytics
      .filter((ip) => geoData.has(ip.ip_address))
      .map((ip) => {
        const geo = geoData.get(ip.ip_address)!;
        const emailCount = ip.email_count || 0;
        const isHighRisk = emailCount > 5;
        const isSuspicious = emailCount > 3 || ip.user_count > 3;
        const [x, y] = toXY(geo.lat, geo.lng);
        return { ip, geo, isHighRisk, isSuspicious, emailCount, x, y };
      });
  }, [ipAnalytics, geoData]);

  const getColor = (isHighRisk: boolean, isSuspicious: boolean) => {
    if (isHighRisk) return '#ef4444';
    if (isSuspicious) return '#eab308';
    return accentColor;
  };

  const getRadius = (emailCount: number) => {
    if (emailCount > 5) return 8;
    if (emailCount > 3) return 6;
    if (emailCount > 1) return 5;
    return 4;
  };

  if (loading && geoData.size === 0) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 flex items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="text-sm text-slate-400">Resolving IP locations…</span>
      </div>
    );
  }

  if (markers.length === 0 && !loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
        <MapPin size={32} className="mx-auto text-slate-700 mb-2" />
        <p className="text-sm text-slate-500">No geo data available for current IPs</p>
      </div>
    );
  }

  const hovered = hoveredIP ? markers.find((m) => m.ip.ip_address === hoveredIP) : null;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <h4 className="font-semibold text-white">IP Locations</h4>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />
            Normal
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-yellow-500" />
            Suspicious
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            High Risk
          </span>
          {loading && <Loader2 size={12} className="animate-spin text-slate-500" />}
        </div>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          className="w-full"
          style={{ backgroundColor: '#0f172a' }}
        >
          <image
            href={WORLD_IMG}
            x="0" y="0"
            width={MAP_W} height={MAP_H}
            opacity="0.15"
            preserveAspectRatio="xMidYMid slice"
          />

          {markers.map((m) => {
            const color = getColor(m.isHighRisk, m.isSuspicious);
            const r = getRadius(m.emailCount);
            const isHovered = hoveredIP === m.ip.ip_address;
            return (
              <g
                key={m.ip.ip_address}
                onMouseEnter={() => setHoveredIP(m.ip.ip_address)}
                onMouseLeave={() => setHoveredIP(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Pulse ring */}
                <circle cx={m.x} cy={m.y} r={r + 6} fill={color} fillOpacity={0.1} />
                {/* Outer glow */}
                <circle cx={m.x} cy={m.y} r={r + 2} fill={color} fillOpacity={isHovered ? 0.4 : 0.2} />
                {/* Main dot */}
                <circle cx={m.x} cy={m.y} r={r} fill={color} fillOpacity={0.85} />
                {isHovered && (
                  <circle cx={m.x} cy={m.y} r={r + 8} fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.6} />
                )}
              </g>
            );
          })}
        </svg>

        {hovered && (
          <div className="absolute top-3 right-3 bg-black/90 border border-slate-700 rounded-xl px-4 py-3 text-xs space-y-1 pointer-events-none z-10 min-w-[180px]">
            <div className="font-mono text-white text-sm">{hovered.ip.ip_address}</div>
            <div className="text-slate-400">{hovered.geo.city}, {hovered.geo.country}</div>
            <div className="flex items-center gap-3 pt-1 border-t border-slate-800 mt-1">
              <span className="text-slate-500">{hovered.emailCount} emails</span>
              <span className="text-slate-500">{hovered.ip.user_count} users</span>
              <span className="text-slate-500">{hovered.ip.event_count} events</span>
            </div>
            {(hovered.ip.emails?.length ?? 0) > 0 && (
              <div className="pt-1 space-y-0.5 max-w-[260px]">
                {hovered.ip.emails!.slice(0, 6).map((email) => (
                  <div key={email} className="text-slate-300 truncate">{email}</div>
                ))}
                {hovered.ip.emails!.length > 6 && (
                  <div className="text-slate-500">+{hovered.ip.emails!.length - 6} more</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
